<template>
  <div class="flex flex-col pb-4">
    <div v-if="periodLabel" class="text-xs text-gray-500 mb-1">
      דוחות Excel לפי התקופה הנבחרת · <b class="text-gray-800">{{ periodLabel }}</b>
    </div>

    <button
      v-for="(item, i) of items"
      :key="item.key"
      type="button"
      class="flex items-center gap-3 py-3.5 text-right"
      :class="{'border-b border-gray-100': i < items.length - 1 || needsTaxId}"
      :disabled="isExporting[item.key]"
      @click="exportFile(item.key)"
    >
      <span class="w-11 h-11 rounded-[13px] shrink-0 flex items-center justify-center" :style="{background: item.tint}">
        <i :class="['pi', item.icon]" class="text-lg" :style="{color: item.fg}"/>
      </span>
      <span class="flex-1 min-w-0">
        <span class="block text-[14px] font-semibold text-gray-900">{{ item.title }}</span>
        <span class="block text-[11.5px] text-gray-500 mt-0.5">{{ item.sub }}</span>
      </span>
      <span
        class="text-[10px] font-bold rounded-full px-2.5 py-1"
        :style="{background: item.badgeBg, color: item.badgeFg}"
      >{{ item.badge }}</span>
      <i v-if="isExporting[item.key]" class="pi pi-spinner pi-spin text-sm text-gray-400"/>
    </button>

    <!-- Tax id input row — shown when the business has no עוסק מורשה on file -->
    <div v-if="needsTaxId" class="flex items-center gap-2 py-3">
      <input
        v-model="taxIdInput"
        inputmode="numeric"
        maxlength="9"
        placeholder="מספר עוסק מורשה / ח״פ"
        class="flex-1 min-w-0 border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:border-primary"
        @input="taxIdInput = taxIdInput.replace(/\D/g, '')"
      />
      <span class="text-[11px] text-gray-400 shrink-0">נדרש לקבצי מס</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {saveFile} from '@app/utils/save-file';
import {computed, reactive, ref} from 'vue';
import {useToast} from 'vue-toastification';

import {ReportsApi} from '@app/api/admin';
import {trackEvent} from '@app/composables/posthog';
import {useAdminStore} from '@app/stores';

type ItemKey = 'appointments' | 'audit' | 'irs';

const props = defineProps<{
  startDate?: string;
  endDate?: string;
  periodLabel?: string;
}>();

const toast = useToast();
const adminStore = useAdminStore();

const isExporting = reactive<Record<ItemKey, boolean>>({
  appointments: false,
  audit: false,
  irs: false,
});

const savedTaxId = ref<string>(adminStore.getBusinessSiteData?.taxId ?? '');
const taxIdInput = ref('');

// Israeli tax-id check digit (Luhn variant) — the Tax Authority simulator
// validates it in every record, so reject bad ids before generating anything.
const isValidIsraeliTaxId = (id: string): boolean => {
  const digits = (id ?? '').replace(/\D/g, '');
  if (digits.length < 5 || digits.length > 9) return false;
  const padded = digits.padStart(9, '0');
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let v = Number(padded[i]) * (i % 2 === 0 ? 1 : 2);
    if (v > 9) v -= 9;
    sum += v;
  }
  return sum % 10 === 0;
};
const needsTaxId = computed(() => !savedTaxId.value);

const items: Array<{
  key: ItemKey; title: string; sub: string; badge: string;
  icon: string; tint: string; fg: string; badgeBg: string; badgeFg: string;
}> = [
  {
    key: 'appointments', title: 'יומן תורים', sub: 'גיליון Excel — שורה לכל תור בתקופה',
    badge: 'XLSX', icon: 'pi-calendar', tint: '#dcfce7', fg: '#15803d', badgeBg: '#dcfce7', badgeFg: '#15803d',
  },
  {
    key: 'audit', title: 'יומן שינויים לביקורת', sub: 'כל יצירה, עדכון, ביטול ומחיקה של תורים',
    badge: 'XLSX', icon: 'pi-shield', tint: '#edeafd', fg: '#6831d0', badgeBg: '#edeafd', badgeFg: '#6831d0',
  },
  {
    key: 'irs', title: 'קבצים לביקורת מס הכנסה', sub: 'מבנה אחיד — קובץ לכל שנה מתחילת הפעילות',
    badge: 'ZIP', icon: 'pi-building', tint: '#fef3c7', fg: '#b45309', badgeBg: '#fef3c7', badgeFg: '#b45309',
  },
];

// Native: opens the share sheet ("Save to Files", Drive, WhatsApp…); web: regular download.
const downloadBlob = (blob: Blob, filename: string) => saveFile(blob, filename);

const exportFile = async (type: ItemKey) => {
  trackEvent('docs_export', {type});
  isExporting[type] = true;
  try {
    if (type === 'irs') {
      const taxId = savedTaxId.value || taxIdInput.value;
      if (!taxId || taxId.length < 5) {
        toast.info('יש להזין מספר עוסק מורשה / ח"פ (5-9 ספרות) לפני הייצוא');
        return;
      }
      if (!isValidIsraeliTaxId(taxId)) {
        toast.error('מספר העוסק שהוזן אינו תקין (ספרת ביקורת שגויה) — בדקו את המספר');
        return;
      }
      const result = await ReportsApi.exportIrsInspection(savedTaxId.value ? undefined : taxId);
      savedTaxId.value = taxId;
      if (result instanceof Blob) {
        if (await downloadBlob(result, 'tax-inspection-files.zip') === 'downloaded') toast.success('הקובץ הורד בהצלחה');
      } else {
        toast.success(`הקבצים בהכנה ויישלחו למייל ${result.email} בסיום`);
      }
      return;
    }

    const blob = type === 'audit'
      ? await ReportsApi.exportAuditLog(props.startDate, props.endDate)
      : await ReportsApi.exportAppointments(props.startDate, props.endDate);
    if (await downloadBlob(blob, type === 'audit' ? 'audit-log.xlsx' : 'appointments.xlsx') === 'downloaded') toast.success('הקובץ הורד בהצלחה');
  } catch (e: any) {
    console.error(e);
    if (e?.status === 404) {
      toast.info('לא נמצאו נתונים לייצוא');
    } else if (e?.message && typeof e.message === 'string' && e?.status === 400) {
      toast.error(e.message);
    } else {
      toast.error('לא הצלחנו להוריד את הקובץ');
    }
  } finally {
    isExporting[type] = false;
  }
};
</script>
